import { useEffect, useRef } from 'react';

export default function GridTokenImageCanvas({ src, zoom = 1, className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !src) return undefined;

    const image = new Image();
    let disposed = false;
    let drawFrame = null;

    const draw = () => {
      if (disposed || !image.complete || !image.naturalWidth || !image.naturalHeight) return;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (!width || !height) return;

      const coverScale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
      const sourceWidth = width / coverScale;
      const sourceHeight = height / coverScale;
      const sourceX = (image.naturalWidth - sourceWidth) / 2;
      const sourceY = (image.naturalHeight - sourceHeight) / 2;
      const requestedRatio = (window.devicePixelRatio || 1) * Math.max(1, Number(zoom) || 1);
      const pixelRatio = Math.min(
        requestedRatio,
        sourceWidth / width,
        sourceHeight / height,
        2048 / Math.max(width, height)
      );
      const pixelWidth = Math.max(1, Math.round(width * pixelRatio));
      const pixelHeight = Math.max(1, Math.round(height * pixelRatio));

      if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;
      }

      const context = canvas.getContext('2d');
      if (!context) return;
      context.setTransform(pixelWidth / width, 0, 0, pixelHeight / height, 0, 0);
      context.clearRect(0, 0, width, height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.save();
      context.filter = 'brightness(0.9)';
      context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        width,
        height
      );
      context.restore();
    };

    image.onload = draw;
    image.src = src;
    if (image.complete) draw();

    const scheduleDraw = () => {
      if (drawFrame !== null) return;
      drawFrame = requestAnimationFrame(() => {
        drawFrame = null;
        draw();
      });
    };
    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(scheduleDraw);
    observer?.observe(canvas);

    return () => {
      disposed = true;
      observer?.disconnect();
      if (drawFrame !== null) cancelAnimationFrame(drawFrame);
      image.onload = null;
    };
  }, [src, zoom]);

  return <canvas ref={canvasRef} className={className} aria-hidden='true' />;
}